// Rug Room Shots: "Regenerate by AI result" (SPEC_AICHECK 5.4.1). A small first step: which AI results to regenerate
// (AI says Fail / Fail or Needs a look / AI score below N). Continue hands a filter to the EXISTING bulk regenerate
// dialog (its preview, cost, daily limit and idempotency), with "Use the AI's notes" ticked there.
// The counts shown here come from read-only GET jobs/{id}/review calls (pageSize 1): every current image of the job
// with the view's style and shape (state All), so they can include approved images, which the next step leaves out
// unless the person includes them.
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, RadioGroup, Radio, FormControlLabel,
  TextField, Stack, CircularProgress,
} from '@mui/material';
import rrsApi from './api';
import { fmtInt, primaryButtonSx, styleName, shapeName } from './common';

const parseBelow = (s) => (/^\d{1,3}$/.test(String(s).trim()) ? Number(String(s).trim()) : NaN);

const AiRegenerateChooser = ({
  open, onClose, jobId, style, shape, config, aiThresholds, onContinue,
}) => {
  const passMin = Number(aiThresholds && aiThresholds.passMin) || 85;
  const [choice, setChoice] = useState('fail');
  const [belowText, setBelowText] = useState(() => String(Math.min(100, Math.max(1, passMin - 15))));
  const [counts, setCounts] = useState(null); // { fail, look } or null while loading / unknown
  const [belowCount, setBelowCount] = useState(null);
  const [belowLoading, setBelowLoading] = useState(false);
  const belowSeq = useRef(0);

  const below = parseBelow(belowText);
  const belowOk = Number.isInteger(below) && below >= 1 && below <= 100;

  // Fail / Needs a look counts once per open.
  useEffect(() => {
    if (!open || !jobId) return undefined;
    let gone = false;
    rrsApi.getReview(jobId, { state: 'all', style, shape, allAttempts: 'false', page: 1, pageSize: 1 })
      .then((res) => { if (!gone && res && res.aiCounts) setCounts(res.aiCounts); })
      .catch(() => { /* counts are a convenience; the next step's preview is exact */ });
    return () => { gone = true; };
  }, [open, jobId, style, shape]);

  // "AI score below N": its count 400 ms after the last change.
  useEffect(() => {
    if (!open || !jobId || !belowOk) { setBelowCount(null); return undefined; }
    const my = belowSeq.current + 1;
    belowSeq.current = my;
    setBelowLoading(true);
    const t = setTimeout(() => {
      rrsApi.getReview(jobId, {
        state: 'all', style, shape, allAttempts: 'false', aiBelow: below, page: 1, pageSize: 1,
      })
        .then((res) => { if (my === belowSeq.current) setBelowCount(res && res.total !== undefined ? Number(res.total) || 0 : null); })
        .catch(() => { if (my === belowSeq.current) setBelowCount(null); })
        .finally(() => { if (my === belowSeq.current) setBelowLoading(false); });
    }, 400);
    return () => clearTimeout(t);
  }, [open, jobId, style, shape, below, belowOk]);

  const fail = counts ? Number(counts.fail) || 0 : null;
  const look = counts ? Number(counts.look) || 0 : null;
  const chosenCount = choice === 'fail' ? fail : (choice === 'not_pass' ? (counts ? fail + look : null) : belowCount);
  const canContinue = (choice !== 'below' || belowOk) && chosenCount !== 0;
  const n = (v) => (v === null || v === undefined ? '…' : fmtInt(v));
  const viewWords = [
    style ? styleName(style, config && config.styles) : 'every style',
    shape ? shapeName(shape, config && config.shapes) : 'every shape',
  ].join(', ');

  const go = () => {
    if (!canContinue) return;
    if (choice === 'below') onContinue({ aiBelow: below });
    else onContinue({ ai: choice });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Regenerate by AI result
        <Typography variant="body2" color="text.secondary">Job #{jobId} · {viewWords}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
          <FormControlLabel value="fail" control={<Radio size="small" />} label={`AI says Fail (${n(fail)})`} />
          <FormControlLabel
            value="not_pass" control={<Radio size="small" />}
            label={`AI says Fail or Needs a look (${counts ? fmtInt(fail + look) : '…'})`}
          />
          <FormControlLabel
            value="below" control={<Radio size="small" />}
            label={(
              <Stack direction="row" spacing={1} alignItems="center">
                <span>AI score below</span>
                <TextField
                  size="small" type="number" value={belowText}
                  onChange={(e) => { setBelowText(e.target.value.slice(0, 3)); setChoice('below'); }}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{ min: 1, max: 100, step: 1, 'aria-label': 'AI score below' }}
                  error={!belowOk}
                  sx={{ width: 84 }}
                />
                {belowOk ? (belowLoading ? <CircularProgress size={14} /> : <span>({n(belowCount)})</span>) : null}
              </Stack>
            )}
          />
        </RadioGroup>
        {choice === 'below' && !belowOk && (
          <Typography variant="caption" color="error.main" display="block">A whole number from 1 to 100.</Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Uses the style and shape filters above. Approved images are left out unless you include them in the next step.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Next you see the exact count and cost, and each image can get the AI&apos;s own note in its prompt.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={go} disabled={!canContinue} sx={primaryButtonSx}>Continue</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiRegenerateChooser;
